import { ISelectedsDespesasRemessas } from "@fl/shared/src/types/despesas";
import { INotificationMessage } from "@fl/shared/src/types/messages";
import { DespesasAttributes } from "@fl/shared/src/types/models/superlogica/despesas";
import { GruposCondominiosAttributes } from "@fl/shared/src/types/models/superlogica/gruposCondominios";
import {
  IDataExcel,
  IDataPDF,
  IExcelColumn,
  IFormFieldReport,
} from "@fl/shared/src/types/reports";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import { Borders as ExcelBorders } from "exceljs";
import moment from "moment";
import numeral from "numeral";
import "numeral/locales/pt-br";
import { TableCell } from "pdfmake/interfaces";
import React, { useEffect, useState } from "react";
import { Col, Dropdown, DropdownButton, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import {
  FILTRAR_POR_DESPESAS,
  FORMA_PAGAMENTO_DESPESAS,
  REMESSA_DESPESAS,
  STATUS_DESPESAS,
} from "../../../components/select/defaultValuesSL";
import SelectComp from "../../../components/select/selectComp";
import { useAuth } from "../../../context/auth";
import useConfirm from "../../../hooks/useConfirm";
import ApiCondominios from "../../../services/suporte/auth/condominios";
import ApiFornecedores from "../../../services/suporte/auth/fornecedores";
import ApiReportFinancial from "../../../services/suporte/auth/reports/financial";
import { dadosPagamento } from "../../../utils/despesasFuncs";
import StringsFuncs from "../../../utils/stringsFuncs";
import DefaultBasePage from "../../default/base";
import DefaultReportPage from "../../default/reports";

const RelatoriosPagamentosPrivatePage: React.FC = () => {
  const { authToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);

  const [groups, setGroups] = useState<GruposCondominiosAttributes[]>([]);
  const [results, setResults] = useState<DespesasAttributes[]>([]);
  const [selecteds, setSelecteds] = useState<ISelectedsDespesasRemessas[]>([]);

  useEffect(() => {
    const listGroups = async () => {
      const result = await new ApiCondominios().listGroups();
      if (result.success) setGroups(result.data);
    };
    listGroups();
  }, []);

  /**
   * Função que cria as opções (value/label) a partir de um grupo,
   * usando lista_condominios + lista_nome_condominios.
   */
  const buildCondominiosOptions = (grupo: GruposCondominiosAttributes) => {
    const ids = grupo.lista_condominios?.split(",") || [];
    const nomes = grupo.lista_nome_condominios?.split(",") || [];
    const options = [];
    for (let i = 0; i < ids.length; i++) {
      const idTrim = ids[i].trim();
      const nomeTrim = nomes[i] ? nomes[i].trim() : `Condomínio ${idTrim}`;
      options.push({ value: idTrim, label: nomeTrim });
    }
    return options;
  };

  /**
   * Busca condomínios por termo (≥ 2 caracteres),
   * retornando um array de { value, label }.
   */
  const searchCondominiosByTerm = async (term: string) => {
    const result = await new ApiCondominios({ term }).find();
    if (result.success) {
      return result.data.map((item: any) => ({
        value: String(item.id_condominio_cond),
        label: item.st_nome_cond,
      }));
    } else {
      return [];
    }
  };

  /**
   * Busca fornecedores por termo (≥ 2 caracteres),
   * retornando um array de { value, label }.
   */
  const searchFornecedoresByTerm = async (term: string) => {
    const result = await new ApiFornecedores({ term }).find();
    if (result.success) {
      return result.data.map((item: any) => ({
        value: String(item.id_contato_con),
        label: item.st_nome_con,
      }));
    } else {
      return [];
    }
  };

  /**
   * Campo customizado para seleção de Grupos e Condomínios.
   * - Se "Todos Condomínios", desabilita selects.
   * - Se seleciona grupos, popula condOptions.
   * - Se digita (≥2 chars), faz busca assíncrona via AsyncSelect.
   */
  const CondominiosComponent: IFormFieldReport = {
    fieldKey: "id_condominios",
    type: "custom",
    // Se precisar injetar mais props externamente, use customProps
    customProps: {
      gruposCondominios: groups,
      promiseCondominiosOptions: () => {},
    },
    render: (methods, field) => {
      const { watch, setValue, control } = methods;
      const { gruposCondominios } = field.customProps ?? {};
      // condOptions: combina condomínios vindos de grupos + busca assíncrona
      const [condOptions, setCondOptions] = useState<
        { value: string; label: string }[]
      >([]);

      // Observa id_condominios no form
      const idCondominios = watch("id_condominios") || [];

      // Botão "Todos Condomínios"
      const allCondominios = () => {
        if (idCondominios.includes("-1")) {
          setValue("id_condominios", []);
          setValue("id_grupos", []);
        } else {
          setValue("id_condominios", ["-1"]);
          setValue("id_grupos", []);
        }
      };

      return (
        <Row>
          <Col>
            <Form.Label>
              Grupo de Condomínios:{" "}
              <span className="link-button" onClick={allCondominios}>
                {idCondominios.includes("-1")
                  ? "Selecionar Condomínios"
                  : "Todos Condomínios"}
              </span>
            </Form.Label>

            {/** Controller do campo "id_grupos" */}
            <Controller
              name="id_grupos"
              control={control}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => {
                // Cria as opções de grupos
                const allGroupOptions = (gruposCondominios ?? []).map(
                  (g: any) => ({
                    value: String(g.id_grupo_cg),
                    label: g.st_grupo_cg ?? `Grupo ${g.id_grupo_cg}`,
                  })
                );

                // Quais grupos estão selecionados?
                const selectedGrupos = allGroupOptions.filter((opt: any) =>
                  (value || []).includes(opt.value)
                );

                const handleChange = (selected: any) => {
                  // 1) Atualiza id_grupos no RHF
                  const vals = selected
                    ? selected.map((s: any) => s.value)
                    : [];
                  onChange(vals);

                  // 2) Monta set de condomínios (IDs) a partir dos grupos escolhidos
                  const newCondominiosSet = new Set<string>();
                  let newCondominiosOptions: {
                    value: string;
                    label: string;
                  }[] = [];

                  vals.forEach((groupIdStr: string) => {
                    const groupId = parseInt(groupIdStr, 10);
                    const groupObj = (gruposCondominios ?? []).find(
                      (g: any) => g.id_grupo_cg === groupId
                    );
                    if (groupObj) {
                      const opcs = buildCondominiosOptions(groupObj);
                      opcs.forEach((opc: any) => {
                        if (!newCondominiosSet.has(opc.value)) {
                          newCondominiosSet.add(opc.value);
                          newCondominiosOptions.push(opc);
                        }
                      });
                    }
                  });

                  // 3) Se não for "todos", define id_condominios com a lista resultante
                  if (!idCondominios.includes("-1")) {
                    const finalArray = Array.from(newCondominiosSet);
                    setValue("id_condominios", finalArray);
                  }

                  // 4) Atualiza estado local
                  setCondOptions(newCondominiosOptions);
                };

                return (
                  <SelectComp
                    isDisabled={idCondominios.includes("-1")}
                    placeholder={
                      idCondominios.includes("-1")
                        ? "Todos Condomínios Selecionados"
                        : "Selecionar Grupos de Condomínios"
                    }
                    isMulti
                    onChange={handleChange}
                    options={allGroupOptions}
                    value={selectedGrupos}
                  />
                );
              }}
            />
          </Col>

          {/* Se não estiver em "todos", exibe o segundo select (AsyncSelect) */}
          {idCondominios[0] !== "-1" && (
            <Col>
              <Form.Label>Condomínios:</Form.Label>
              <Controller
                name="id_condominios"
                control={control}
                defaultValue={[]}
                render={({ field: { onChange, value } }) => {
                  // De condOptions, filtra só os que constam em "value"
                  const selectedCondominios = condOptions.filter((opt) =>
                    (value || []).includes(opt.value)
                  );

                  // Ao selecionar/deselecionar no AsyncSelect
                  const handleChangeConds = (selected: any) => {
                    const newVals = selected
                      ? selected.map((s: any) => s.value)
                      : [];

                    // Mesclar as novas seleções em condOptions
                    const newlySelected =
                      selected?.map((s: any) => ({
                        value: s.value,
                        label: s.label,
                      })) || [];

                    // Evita duplicatas
                    const updated = [
                      ...condOptions,
                      ...newlySelected.filter(
                        (newOpt: any) =>
                          !condOptions.some(
                            (oldOpt) => oldOpt.value === newOpt.value
                          )
                      ),
                    ];
                    setCondOptions(updated);

                    // Atualiza no RHF
                    onChange(newVals);
                  };

                  // Carrega opções via busca assíncrona
                  const loadOptions = async (inputValue: string) => {
                    // Se digitar menos de 2 chars, apenas retorna o condOptions
                    if (inputValue.length < 2) {
                      return condOptions;
                    }
                    const fetched = await searchCondominiosByTerm(inputValue);
                    // Filtra para não duplicar os já selecionados
                    const fetchedFiltrado = fetched.filter(
                      (opt: any) => !value.includes(opt.value)
                    );

                    // Mescla no condOptions
                    const merged = [
                      ...condOptions,
                      ...fetchedFiltrado.filter(
                        (newOpt: any) =>
                          !condOptions.some(
                            (oldOpt) => oldOpt.value === newOpt.value
                          )
                      ),
                    ];
                    setCondOptions(merged);

                    return merged;
                  };

                  return (
                    <AsyncSelect
                      isMulti
                      loadOptions={loadOptions}
                      defaultOptions={condOptions}
                      onChange={handleChangeConds}
                      value={selectedCondominios}
                      noOptionsMessage={() => "Sem resultados"}
                      placeholder={
                        idCondominios.includes("-1")
                          ? "Todos Condomínios Selecionados"
                          : "Digite pelo menos 2 caracteres..."
                      }
                    />
                  );
                }}
              />
            </Col>
          )}
        </Row>
      );
    },
    colSize: 12,
  };

  /**
   * Campo para seleção de Fornecedores.
   * - Se "Todos Fornecedores", desabilita selects.
   * - Se digita (≥2 chars), faz busca assíncrona via AsyncSelect.
   */
  const FornecedoresComponent: IFormFieldReport = {
    fieldKey: "id_fornecedores",
    type: "custom",
    render: (methods, field) => {
      const { watch, setValue, control } = methods;
      const idFornecedores = watch("id_fornecedores") || [];
      // Botão "Todos Fornecedores"
      const allFornecedores = () => {
        if (idFornecedores.includes("-1")) {
          setValue("id_fornecedores", []);
        } else {
          setValue("id_fornecedores", ["-1"]);
        }
      };

      return (
        <Col>
          <Form.Label>
            Fornecedores:{" "}
            <span className="link-button" onClick={allFornecedores}>
              {idFornecedores.includes("-1")
                ? "Selecionar Fornecedores"
                : "Todos Fornecedores"}
            </span>
          </Form.Label>

          <Controller
            name="id_fornecedores"
            control={control}
            defaultValue={[]}
            render={({ field: { onChange, value } }) => {
              const [fornOptions, setFornOptions] = useState<
                { value: string; label: string }[]
              >([]);

              const selectedForns = fornOptions.filter((opt) =>
                (value || []).includes(opt.value)
              );

              const handleChangeForns = (selected: any) => {
                const newVals = selected
                  ? selected.map((s: any) => s.value)
                  : [];
                // Vamos também mesclar esses itens no fornOptions, para que apareçam como selecionados
                const newlySelected =
                  selected?.map((s: any) => ({
                    value: s.value,
                    label: s.label,
                  })) || [];

                // Evita duplicar
                const updated = [
                  ...fornOptions,
                  ...newlySelected.filter(
                    (newOpt: any) =>
                      !fornOptions.some(
                        (oldOpt) => oldOpt.value === newOpt.value
                      )
                  ),
                ];
                setFornOptions(updated);

                onChange(newVals); // Atualiza RHF
              };

              // Carrega opções via busca assíncrona
              const loadOptions = async (inputValue: string) => {
                // Se digitar menos de 2 chars, apenas retorna o condOptions
                if (inputValue.length >= 2) {
                  const fetched = await searchFornecedoresByTerm(inputValue);
                  return fetched;
                }
              };

              return (
                <AsyncSelect
                  isMulti
                  isDisabled={idFornecedores.includes("-1")}
                  loadOptions={loadOptions}
                  onChange={handleChangeForns}
                  value={selectedForns}
                  noOptionsMessage={() => "Sem resultados"}
                  placeholder={
                    idFornecedores.includes("-1")
                      ? "Todos Fornecedores Selecionados"
                      : "Digite pelo menos 2 caracteres..."
                  }
                />
              );
            }}
          />
        </Col>
      );
    },
    colSize: 12,
  };

  // Demais campos do relatório
  const fields: IFormFieldReport[] = [
    {
      fieldKey: "dt_inicio",
      label: "Data Inicial",
      type: "date",
      validation: { required: "Data inicial é obrigatória" },
      colSize: 3,
    },
    {
      fieldKey: "dt_fim",
      label: "Data Final",
      type: "date",
      validation: { required: "Data final é obrigatória" },
      colSize: 3,
    },
    {
      fieldKey: "status",
      label: "Status dos Pagamentos",
      type: "select",
      placeholder: "Selecione...",
      options: STATUS_DESPESAS,
      colSize: 3,
    },
    {
      fieldKey: "despesas",
      label: "Filtrar Por",
      type: "select",
      placeholder: "Selecione...",
      options: FILTRAR_POR_DESPESAS,
      colSize: 3,
    },
    {
      fieldKey: "formas",
      label: "Formas de Pagamento",
      type: "select",
      placeholder: "Selecione...",
      selectProps: {
        isMulti: true,
      },
      options: FORMA_PAGAMENTO_DESPESAS,
      colSize: 6,
    },
    {
      fieldKey: "remessas",
      label: "Remessas",
      type: "select",
      placeholder: "Selecione...",
      selectProps: {
        isMulti: false,
      },
      options: REMESSA_DESPESAS,
      colSize: 6,
    },
    // Campo customizado de Condomínios
    CondominiosComponent,
    // Campo customizado de Fornecedores
    FornecedoresComponent,
  ];

  // Chamado ao clicar em "Gerar Relatório"
  const onRequestReport = async (data: any) => {
    try {
      setLoading(true);

      const despesa = data.despesas
        ? FILTRAR_POR_DESPESAS.find((i) => i.value === data.despesas)?.field
        : data.despesas;

      const remessa = data.status
        ? REMESSA_DESPESAS.find((i) => i.value === data.remessas)?.field
        : data.remessas;

      const status = data.status
        ? STATUS_DESPESAS.find((i) => i.value === data.status)?.label
        : data.status;

      const query =
        `?despesas=${despesa}&` +
        `dt_inicio=${moment(data.dt_inicio, "YYYY-MM-DD").format("MM/DD/YYYY")}&` +
        `dt_fim=${moment(data.dt_fim, "YYYY-MM-DD").format("MM/DD/YYYY")}&` +
        `id_condominios=${data.id_condominios.toString()}&` +
        `id_fornecedores=${data.id_fornecedores.toString()}&` +
        `formas=${data.formas.toString()}&` +
        `remessas=${remessa}&` +
        `status=${status}`;

      const result = await new ApiReportFinancial().payments(query);
      if (result.success) {
        setResults(result.data);
        const despesas: DespesasAttributes[] = result.data;
        const selects: ISelectedsDespesasRemessas[] = [];
        despesas.map((item) => {
          selects.push({
            id_condominio_cond: item.id_condominio_cond?.toString(),
            id_despesa_des: item.id_despesa_des?.toString(),
            id_parcela_pdes: item.id_parcela_pdes?.toString(),
            selected: true,
            netValue: item.vl_valor_pdes ? item.vl_valor_pdes : 0,
            grossValue: item.vl_valorbruto_pdes ? item.vl_valorbruto_pdes : 0,
          });
        });
        setSelecteds([...selects]);
      }

      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  const columnsTable: ITableDataColumns[] = [
    {
      columnKey: "select",
      label: "Selecionar",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        const selected = selecteds.find(
          (s) =>
            s.id_parcela_pdes === item.id_parcela_pdes &&
            s.id_despesa_des === item.id_despesa_des
        );

        if (selected) {
          return (
            <Form.Check
              type="checkbox"
              id={`default-${selected?.id_parcela_pdes}`}
              checked={selected?.selected}
              onChange={() => {
                const idx = selecteds.findIndex(
                  (s) =>
                    s.id_parcela_pdes === selected.id_parcela_pdes &&
                    s.id_despesa_des === selected.id_despesa_des
                );
                if (idx >= 0) {
                  const copy = [...selecteds];
                  copy[idx] = { ...copy[idx], selected: !copy[idx].selected };
                  setSelecteds(copy);
                }
              }}
            />
          );
        }
      },
    },
    {
      columnKey: "id_despesa_des",
      label: "ID Desp.",
      pk: true,
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        return item.id_despesa_des ? item.id_despesa_des.toString() : "";
      },
      sortValue: (item: DespesasAttributes) => {
        return item.id_despesa_des
          ? parseInt(item.id_despesa_des.toString())
          : "";
      },
    },
    {
      columnKey: "id_parcela_pdes",
      label: "ID Parc.",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        return item.id_parcela_pdes ? item.id_parcela_pdes.toString() : "";
      },
      sortValue: (item: DespesasAttributes) => {
        return item.id_parcela_pdes
          ? parseInt(item.id_parcela_pdes.toString())
          : "";
      },
    },
    {
      columnKey: "fl_liquidado_pdes",
      label: "Situação",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        return item.fl_liquidado_pdes?.toString() === "0" ? "Aberto" : "Pago";
      },
      sortValue: (item: DespesasAttributes) => {
        return item.fl_liquidado_pdes;
      },
    },
    {
      columnKey: "id_remessa",
      label: "Remessa",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        const remessa = REMESSA_DESPESAS.find((r) =>
          r.codigos.includes(item.fl_remessastatus_pdes!.toString())
        );

        return remessa ? remessa.label : item.fl_remessastatus_pdes;
      },
      sortValue: (item: DespesasAttributes) => {
        const remessa = REMESSA_DESPESAS.find((r) =>
          r.codigos.includes(item.fl_remessastatus_pdes!.toString())
        );

        return remessa?.label.toLowerCase() || "";
      },
    },
    {
      columnKey: "forma",
      label: "Forma Pagto.",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        const forma = FORMA_PAGAMENTO_DESPESAS.find(
          (f) => f.value === item.id_forma_pag?.toString()
        );

        return forma ? forma.label : item.id_forma_pag;
      },
      sortValue: (item: DespesasAttributes) => {
        const forma = FORMA_PAGAMENTO_DESPESAS.find(
          (f) => f.value === item.id_forma_pag?.toString()
        );

        return forma?.label.toLowerCase() || "";
      },
    },
    {
      columnKey: "dados",
      label: "Dados Pagto.",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        const grupos: string[] = [];
        results.map((item: DespesasAttributes) => {
          groups.map((g) => {
            if (g.lista_condominios && g.lista_condominios.includes(",")) {
              if (
                g.st_grupo_cg!.trim().toLocaleLowerCase().includes("banco") &&
                g.lista_condominios
                  .split(",")
                  .includes(item.id_condominio_cond!.toString())
              ) {
                grupos.push(g.st_grupo_cg!);
              }
            }
          });
        });
        const result = dadosPagamento(item, grupos.join(","));

        return result.element;
      },
      sortValue: (item: DespesasAttributes) => {
        const grupos: string[] = [];
        results.map((item: DespesasAttributes) => {
          groups.map((g) => {
            if (g.lista_condominios && g.lista_condominios.includes(",")) {
              if (
                g.st_grupo_cg!.trim().toLocaleLowerCase().includes("banco") &&
                g.lista_condominios
                  .split(",")
                  .includes(item.id_condominio_cond!.toString())
              ) {
                grupos.push(g.st_grupo_cg!);
              }
            }
          });
        });

        const result = dadosPagamento(item, grupos.join(","));
        const paddedStatus = String(result.status).padStart(3, "0");
        const paddedMethod = String(result.method.value).padStart(3, "0");
        return `${paddedStatus}~${paddedMethod}~${result.message}`;
      },
    },
    {
      columnKey: "vencto",
      label: "Vencimento",
      alignText: "text-center",
      render: (item: DespesasAttributes) => {
        return moment(item.dt_vencimento_pdes, "MM/DD/YYYY").format(
          "DD/MM/YYYY"
        );
      },
      sortValue: (item: DespesasAttributes) => {
        return moment(item.dt_vencimento_pdes, "MM/DD/YYYY");
      },
    },
    {
      columnKey: "st_documento_des",
      label: "Documento",
      alignText: "text-center",
    },
    {
      columnKey: "st_fantasia_cond",
      label: "Condomínio",
      alignText: "text-start",
    },
    {
      columnKey: "st_nome_con",
      label: "Fornecedor",
      alignText: "text-start",
    },
    {
      columnKey: "st_nomerecebedor_fav",
      label: "Favorecido",
      alignText: "text-start",
    },
    {
      columnKey: "bruto",
      label: "Bruto",
      alignText: "text-end",
      render: (item: DespesasAttributes) => {
        return numeral(
          item.vl_valorbruto_pdes?.toString().replace(".", ",")
        ).format("#,##0.00");
      },
      sortValue: (item: DespesasAttributes) => {
        return item.vl_valorbruto_pdes
          ? parseFloat(item.vl_valorbruto_pdes.toString())
          : 0;
      },
    },
    {
      columnKey: "liquido",
      label: "Líquido",
      alignText: "text-end",
      render: (item: DespesasAttributes) => {
        return numeral(item.vl_valor_pdes?.toString().replace(".", ",")).format(
          "#,##0.00"
        );
      },
      sortValue: (item: DespesasAttributes) => {
        return item.vl_valor_pdes
          ? parseFloat(item.vl_valor_pdes.toString())
          : 0;
      },
    },
  ];

  const totalizer = () => {
    var sumGrossTotalGeneral = 0;
    var sumNetTotalGeneral = 0;
    var sumGrossTotalSelected = 0;
    var sumNetTotalSelected = 0;

    results.forEach((item) => {
      sumNetTotalGeneral += item.vl_valor_pdes
        ? parseFloat(item.vl_valor_pdes.toString())
        : 0;

      sumGrossTotalGeneral += item.vl_valorbruto_pdes
        ? parseFloat(item.vl_valorbruto_pdes.toString())
        : 0;
    });

    selecteds.forEach((item) => {
      sumGrossTotalSelected += item.selected
        ? parseFloat(item.grossValue.toString())
        : 0;
      sumNetTotalSelected += item.selected
        ? parseFloat(item.netValue.toString())
        : 0;
    });

    const grupos: string[] = [];
    results.map((item: DespesasAttributes) => {
      groups.map((g) => {
        if (g.lista_condominios && g.lista_condominios.includes(",")) {
          if (
            g.st_grupo_cg!.trim().toLocaleLowerCase().includes("banco") &&
            g.lista_condominios
              .split(",")
              .includes(item.id_condominio_cond!.toString())
          ) {
            grupos.push(g.st_grupo_cg!);
          }
        }
      });
    });

    const status = results.map((item) => {
      return dadosPagamento(item, grupos.join(","));
    });

    return (
      <div style={{ fontSize: "14px" }}>
        <Row className="my-1 hover-bold">
          <Col>Totalizador (Situações)</Col>

          <Col style={{ textAlign: "center" }}>
            Corretos:{" "}
            {status.filter((item) => item.status.toString() === "1").length}
          </Col>

          <Col style={{ textAlign: "center" }}>
            Com erro(s):{" "}
            {status.filter((item) => item.status.toString() === "2").length}
          </Col>

          <Col style={{ textAlign: "center" }}>
            Com Alerta(s):{" "}
            {
              status.filter(
                (item) =>
                  item.status.toString() === "3" ||
                  item.status.toString() === "-1"
              ).length
            }
          </Col>
        </Row>

        <Row className="my-1 hover-bold">
          <Col>Totalizador (Geral)</Col>

          <Col style={{ textAlign: "center" }}>{results.length}</Col>

          <Col style={{ textAlign: "center" }}>
            Valor Bruto:{" "}
            {numeral(parseFloat(sumGrossTotalGeneral.toString())).format(
              "$ #,##0.00"
            )}
          </Col>

          <Col style={{ textAlign: "center" }}>
            Valor Líquido:{" "}
            {numeral(parseFloat(sumNetTotalGeneral.toString())).format(
              "$ #,##0.00"
            )}
          </Col>
        </Row>

        <Row className="my-1 hover-bold">
          <Col>Totalizador (Selecionados)</Col>

          <Col style={{ textAlign: "center" }}>
            {selecteds.filter((item) => item.selected).length}
          </Col>

          <Col style={{ textAlign: "center" }}>
            Valor Bruto:{" "}
            {numeral(parseFloat(sumGrossTotalSelected.toString())).format(
              "$ #,##0.00"
            )}
          </Col>

          <Col style={{ textAlign: "center" }}>
            Valor Líquido:{" "}
            {numeral(parseFloat(sumNetTotalSelected.toString())).format(
              "$ #,##0.00"
            )}
          </Col>
        </Row>

        <Row className="my-2">
          <Col className="text-end">
            <DropdownButton
              size="sm"
              id="dropdown-basic-button"
              title="Ações"
              variant="outline-dark"
              data-bs-theme="dark"
            >
              <Dropdown.Item
                onClick={() => {
                  const items: ISelectedsDespesasRemessas[] = selecteds.map(
                    (item) => {
                      return { ...item, selected: true };
                    }
                  );
                  setSelecteds([...items]);
                }}
              >
                Selecionar Todos
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() => {
                  const items: ISelectedsDespesasRemessas[] = selecteds.map(
                    (item) => {
                      return { ...item, selected: false };
                    }
                  );
                  setSelecteds([...items]);
                }}
              >
                Desselecionar Todos
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() => {
                  const data = [...selecteds];

                  status
                    .filter((st) => st.status === 2)
                    .forEach((st) => {
                      const index = data.findIndex(
                        (s) =>
                          s.id_despesa_des === st.data.id_despesa_des &&
                          s.id_parcela_pdes === st.data.id_parcela_pdes
                      );
                      data[index].selected = false;
                    });

                  setSelecteds([...data]);
                }}
              >
                Desselecionar com Erro
              </Dropdown.Item>

              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() =>
                  handleShowMarkRemessas(
                    "Marcar remessas",
                    `Você tem certeza que deseja enviar <strong>${selecteds.filter((item) => item.selected).length} de ${results.length}</strong> despesas, para <b>marcar remessa</b>?`,
                    true
                  )
                }
              >
                Enviar selecionados para{" "}
                <i>
                  <b>Marcar Remessa</b>
                </i>
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() =>
                  handleShowMarkRemessas(
                    "Marcar remessas",
                    `Você tem certeza que deseja enviar <strong>${selecteds.filter((item) => item.selected).length} de ${results.length}</strong> despesas, para <b>desmarcar remessa</b>?`,
                    false
                  )
                }
              >
                Enviar selecionados para{" "}
                <i>
                  <b>Desmarcar Remessa</b>
                </i>
              </Dropdown.Item>
            </DropdownButton>
          </Col>
        </Row>
      </div>
    );
  };

  const buildExportDataPDF = (): IDataPDF => {
    const columns: TableCell[] = [
      {
        text: "#",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "ID Desp.",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
      },
      {
        text: "ID Parc.",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
      },
      {
        text: "Situação",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Remessa",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Forma Pagto.",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
      },
      {
        text: "Dados Pagto.",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Vencto.",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Doc.",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Condomínio",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Fornecedor",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Favorecido",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Bruto",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: "Líquido",
        fillColor: "gray",
        color: "black",
        alignment: "center",
        fontSize: 8,
        bold: true,
        margin: [0, 5, 0, 0],
      },
    ];

    const data: TableCell[][] = [];
    let counter = 0;
    let totalBruto = 0;
    let totalLiquido = 0;

    selecteds
      .filter((item) => item.selected)
      .forEach((item) => {
        const despesa = results.find(
          (d) =>
            d.id_despesa_des?.toString() === item.id_despesa_des &&
            d.id_parcela_pdes?.toString() === item.id_parcela_pdes
        );
        if (despesa) {
          const remessa = REMESSA_DESPESAS.find((r) =>
            r.codigos.includes(despesa.fl_remessastatus_pdes!.toString())
          );

          const forma = FORMA_PAGAMENTO_DESPESAS.find(
            (f) => f.value === despesa.id_forma_pag?.toString()
          );

          const grupos: string[] = [];
          results.map((ds: DespesasAttributes) => {
            groups.map((g) => {
              if (g.lista_condominios && g.lista_condominios.includes(",")) {
                if (
                  g.st_grupo_cg!.trim().toLocaleLowerCase().includes("banco") &&
                  g.lista_condominios
                    .split(",")
                    .includes(ds.id_condominio_cond!.toString())
                ) {
                  grupos.push(g.st_grupo_cg!);
                }
              }
            });
          });
          const dados = dadosPagamento(despesa, grupos.join(","));
          var dadosType = "";

          switch (dados.status) {
            case 1:
              dadosType = "OK";
              break;
            case 2:
              dadosType = "ERRO";
              break;
            default:
              dadosType = "ATENÇÃO";
              break;
          }

          totalBruto += despesa.vl_valorbruto_pdes
            ? parseFloat(despesa.vl_valorbruto_pdes.toString())
            : 0;
          totalLiquido += despesa.vl_valor_pdes
            ? parseFloat(despesa.vl_valor_pdes.toString())
            : 0;

          data.push([
            { text: (counter + 1).toString(), alignment: "center" },
            { text: despesa.id_despesa_des, alignment: "center" },
            { text: despesa.id_parcela_pdes, alignment: "center" },
            {
              text:
                despesa.fl_liquidado_pdes?.toString() === "0"
                  ? "Aberto"
                  : "Pago",
              alignment: "center",
            },
            {
              text: remessa ? remessa.label : despesa.fl_remessastatus_pdes,
              alignment: "center",
            },
            {
              text: forma ? forma.label : despesa.id_forma_pag,
              alignment: "center",
            },
            {
              text: `${dadosType} ${dados.message !== "" ? `: ${dados.message}` : ""}`,
            },
            {
              text: moment(despesa.dt_vencimento_pdes, "MM/DD/YYYY").format(
                "DD/MM/YYYY"
              ),
              alignment: "center",
            },
            { text: despesa.st_documento_des, alignment: "center" },
            { text: despesa.st_fantasia_cond },
            { text: despesa.st_nome_con },
            { text: despesa.st_nomerecebedor_fav },
            {
              text: numeral(
                despesa.vl_valorbruto_pdes?.toString().replace(".", ",")
              ).format("#,##0.00"),
              alignment: "right",
            },
            {
              text: numeral(
                despesa.vl_valor_pdes?.toString().replace(".", ",")
              ).format("#,##0.00"),
              alignment: "right",
            },
          ]);

          counter += 1;
        }
      });

    const totalRow: TableCell[] = [
      {
        text: `TOTAL (${counter} linhas)`,
        colSpan: 12,
        alignment: "right",
        bold: true,
      },
      "", // placeholders para colSpan
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      {
        text: numeral(totalBruto).format("0,0.00"),
        alignment: "right",
        bold: true,
      },
      {
        text: numeral(totalLiquido).format("0,0.00"),
        alignment: "right",
        bold: true,
      },
    ];

    data.push(totalRow);

    const dataReport: IDataPDF = {
      headerTitle: "Relatório Financeiro - Pagamentos",
      pageOrientation: "landscape",
      content: {
        style: "table",
        table: {
          dontBreakRows: true,
          headerRows: 1,
          body: [columns, ...data],
        },
        layout: {
          fillColor: (rowIndex: number) => {
            return rowIndex % 2 === 1 ? "#fff" : null;
          },
        },
      },
      styles: {
        table: {
          fontSize: 6,
        },
      },
      footerCols: [
        {
          text: `Total de Despesas: ${counter}`,
          alignment: "left",
          fontSize: 8,
          bold: true,
          margin: [20, 0, 0, 10],
        },
        {
          text: `Total Bruto: ${numeral(totalBruto).format("$ 0,0.00")}`,
          alignment: "center",
          fontSize: 8,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        {
          text: `Total Líquido: ${numeral(totalLiquido).format("$ 0,0.00")}`,
          alignment: "right",
          fontSize: 8,
          bold: true,
          margin: [0, 0, 20, 10],
        },
      ],
    };

    return dataReport;
  };

  const getExportDataExcel = (): IDataExcel => {
    const border: Partial<ExcelBorders> = {
      bottom: { color: { argb: "000000" }, style: "thin" },
      left: { color: { argb: "000000" }, style: "thin" },
      right: { color: { argb: "000000" }, style: "thin" },
      top: { color: { argb: "000000" }, style: "thin" },
    };

    const columns: IExcelColumn[] = [
      {
        header: "#",
        font: { bold: true },
        width: 10,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
        },
      },
      {
        header: "ID Desp.",
        font: { bold: true },
        width: 10,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
        },
      },
      {
        header: "ID Parc.",
        font: { bold: true },
        width: 10,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
        },
      },
      {
        header: "Situação",
        font: { bold: true },
        width: 12,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
        },
      },
      {
        header: "Remessa",
        font: { bold: true },
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
        },
      },
      {
        header: "Forma Pagto.",
        font: { bold: true },
        width: 17,
        style: { alignment: { wrapText: true, vertical: "middle" } },
      },
      {
        header: "Dados Pagto.",
        font: { bold: true },
        width: 30,
        style: { alignment: { wrapText: true, vertical: "middle" } },
      },
      {
        header: "Vencto.",
        font: { bold: true },
        width: 12,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          },
          numFmt: "DD/MM/YYYY",
        },
      },
      {
        header: "Doc.",
        font: { bold: true },
        width: 12,
        style: { alignment: { wrapText: true, vertical: "middle" } },
      },
      {
        header: "Condomínio",
        font: { bold: true },
        width: 50,
        style: { alignment: { wrapText: true, vertical: "middle" } },
      },
      {
        header: "Fornecedor",
        font: { bold: true },
        width: 50,
        style: { alignment: { wrapText: true, vertical: "middle" } },
      },
      {
        header: "Favorecido",
        font: { bold: true },
        width: 50,
        style: { alignment: { wrapText: true, vertical: "middle" } },
      },
      {
        header: "Bruto",
        font: { bold: true },
        width: 12,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "right",
          },
          numFmt: "#,##.00",
        },
      },
      {
        header: "Líquido",
        font: { bold: true },
        width: 12,
        style: {
          alignment: {
            wrapText: true,
            vertical: "middle",
            horizontal: "right",
          },
          numFmt: "#,##.00",
        },
      },
    ];

    const rows: any[][] = [];
    let counter = 0;
    let totalBruto = 0;
    let totalLiquido = 0;

    selecteds
      .filter((item) => item.selected)
      .forEach((item) => {
        const despesa = results.find(
          (d: DespesasAttributes) =>
            d.id_despesa_des?.toString() === item.id_despesa_des &&
            d.id_parcela_pdes?.toString() === item.id_parcela_pdes
        );

        if (despesa) {
          const remessa = REMESSA_DESPESAS.find((r) =>
            r.codigos.includes(despesa.fl_remessastatus_pdes!.toString())
          );
          const forma = FORMA_PAGAMENTO_DESPESAS.find(
            (f) => f.value === despesa.id_forma_pag?.toString()
          );

          const grupoBancos: string[] = [];
          results.forEach((ds: DespesasAttributes) => {
            groups.forEach((g) => {
              if (
                g.st_grupo_cg?.toLowerCase().includes("banco") &&
                g.lista_condominios
                  ?.split(",")
                  .includes(ds.id_condominio_cond?.toString() || "")
              ) {
                grupoBancos.push(g.st_grupo_cg);
              }
            });
          });
          const dados = dadosPagamento(despesa, grupoBancos.join(","));

          let dadosType = "";
          switch (dados.status) {
            case 1:
              dadosType = "OK";
              break;
            case 2:
              dadosType = "ERRO";
              break;
            default:
              dadosType = "ATENÇÃO";
              break;
          }

          const vlBruto = despesa.vl_valorbruto_pdes
            ? parseFloat(despesa.vl_valorbruto_pdes.toString())
            : 0;
          const vlLiquido = despesa.vl_valor_pdes
            ? parseFloat(despesa.vl_valor_pdes.toString())
            : 0;
          totalBruto += vlBruto;
          totalLiquido += vlLiquido;

          rows.push([
            counter + 1,
            despesa.id_despesa_des
              ? parseInt(despesa.id_despesa_des.toString())
              : "",
            despesa.id_parcela_pdes
              ? parseInt(despesa.id_parcela_pdes.toString())
              : "",
            despesa.fl_liquidado_pdes?.toString() === "0" ? "Aberto" : "Pago",
            remessa ? remessa.label : despesa.fl_remessastatus_pdes,
            forma ? forma.label : despesa.id_forma_pag,
            `${dadosType}${dados.message ? `: ${dados.message}` : ""}`,
            moment(despesa.dt_vencimento_pdes, "MM/DD/YYYY").format(
              "DD/MM/YYYY"
            ),
            despesa.st_documento_des || "",
            despesa.st_fantasia_cond || "",
            despesa.st_nome_con || "",
            despesa.st_nomerecebedor_fav || "",
            parseFloat(vlBruto.toString()),
            parseFloat(vlLiquido.toString()),
          ]);

          counter++;
        }
      });

    rows.push([
      `TOTAL (${counter} linhas)`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      {
        formula: `SUM(M2:M${counter + 1})`,
        result: totalBruto,
      },
      {
        formula: `SUM(N2:N${counter + 1})`,
        result: totalLiquido,
        border,
        bold: true,
        style: {
          border,
          bold: true,
        },
      },
    ]);

    const dataReport: IDataExcel = {
      headerTitle: "Relatório Financeiro - Pagamentos",
      sheetName: "Relatório Pagamentos",
      columns,
      rows,
      pageSetup: {
        fitToPage: true,
        fitToHeight: 100,
        orientation: "landscape",
        paperSize: 9,
        margins: {
          left: 0.7,
          right: 0.7,
          top: 0.75,
          bottom: 0.75,
          header: 0.3,
          footer: 0.3,
        },
        printArea: new StringsFuncs().dimmensionsArea(
          columns.length,
          rows.length
        ),
        printTitlesRow: "1:1",
      },
      headerFooter: {
        oddFooter: `&"Arial,regular"&5&L&BTotal de Despesas: ${counter}\n&L&F&C&BTotal Bruto: ${numeral(parseFloat(totalBruto.toString())).format("$ 0,000.00")}\n&CPágina &P de &N&R&BTotal Líquido: ${numeral(parseFloat(totalLiquido.toString())).format("$ 0,000.00")}\n&RGerado em &D às &T, por ${authToken?.nome}`,
      },
    };

    return dataReport;
  };

  const submitSendMarkRemessas = async (mark: boolean) => {};

  const {
    handleShow: handleShowMarkRemessas,
    ConfirmModalComponent: ConfirmModalMarkRemessas,
  } = useConfirm<boolean>({
    onConfirm: (mark) => {
      submitSendMarkRemessas(mark);
    },
  });

  return (
    <DefaultBasePage
      title="Relatórios Financeiro"
      subtitle="Pagamentos"
      h3
      loading={loading}
      message={messages}
    >
      <div className="text-justify">
        <DefaultReportPage
          fields={fields}
          onRequestReport={onRequestReport}
          resultsData={results}
          columnsTable={columnsTable}
          defaultValues={{
            dt_inicio: moment().add(-1, "days").format("YYYY-MM-DD"),
            dt_fim: moment().format("YYYY-MM-DD"),
            status: "2",
            despesas: "6",
            formas: ["0", "8", "9", "10", "12"],
            remessas: "2",
            id_condominios: ["-1"],
            id_fornecedores: ["-1"],
          }}
          totalizerElement={totalizer()}
          totalizerPosition="mirror"
          setLoading={setLoading}
          exportButtonTitle="Exportar (Selecionados)"
          getExportDataPDF={buildExportDataPDF}
          setMessagesExport={setMessages}
          getExportDataExcel={getExportDataExcel}
          reportName="Financeiro (Pagamentos)"
        />
      </div>

      {ConfirmModalMarkRemessas}
    </DefaultBasePage>
  );
};

export default RelatoriosPagamentosPrivatePage;
